import React, {
    forwardRef,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    useEffect,
} from 'react';
import axios from 'axios';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Popover,
    Typography,
    Switch,
    Menu,
    MenuItem,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ViewListIcon from '@mui/icons-material/ViewList';
import classnames from 'classnames';

import { useTooltips } from 'src/components/ReviewViewer/Tooltips/hooks/useTooltips';
import {
    OnAddTooltipCordType,
    TooltipEditorModes,
    TooltipSizesType,
    TooltipsType,
} from 'src/components/ReviewViewer/Tooltips/types';
import { AddTooltip } from 'src/components/ReviewViewer/Tooltips/components/AddTooltip';
import { DeleteTooltip } from 'src/components/ReviewViewer/Tooltips/components/DeleteTooltip';
import { mockTooltips } from 'src/components/ReviewViewer/Tooltips/constants';
import { Tooltip } from 'src/components/ReviewViewer/Tooltips/components/Tooltip';
import { clientDomain, functionUri } from 'src/constants';
import { WithAdminMode } from 'src/helpers/hocs/withAdminMode';
import { GoToFrameType } from 'src/components/ReviewViewer/types';
import { drawSizes } from 'src/components/ReviewViewer/Tooltips/helpers';
import { ReviewCategoriesResponse } from 'src/types/reviewsCategoriesResponse';

import styles from './Tooltips.module.scss';

type Props = {
    canvasRef: React.RefObject<HTMLCanvasElement>;
    currentFrameIndex: number;
    framesMaxIndex: number;
    setFrame: GoToFrameType;
    reverseMove?: boolean;
    initialTooltips?: TooltipsType;
    onChangeMode?: (mode: TooltipEditorModes) => void;
    onAddNewTooltipCord?: OnAddTooltipCordType;
};

const Tooltips = forwardRef<{}, Props>(
    (
        {
            canvasRef,
            initialTooltips = mockTooltips,
            onChangeMode = () => {},
            currentFrameIndex,
            framesMaxIndex,
            setFrame,
            onAddNewTooltipCord,
        },
        Ref,
    ) => {
        const anchorRef = useRef<HTMLButtonElement>(null);

        const {
            tooltips,
            newTooltip,
            createNewTooltip,
            addNewTooltipCoords,
            saveNewTooltip,
            removeNewTooltip,
            deleteTooltip,
            options: { hideWhileSpinning, changeVisibilityNearExtremeFrames },
            changeOptions,
        } = useTooltips(initialTooltips, canvasRef, onAddNewTooltipCord);

        const [isOpen, setIsOpen] = useState<boolean>(false);
        const [isMasksEnable, setIsMasksEnable] = useState<boolean>(false);
        const [isMasksActive, setisMasksActive] = useState<boolean>(false);
        const [isShowTollbar, seIsShowTollbar] = useState<boolean>(true);
        const [isSettingsOpen, seIsSettingsOpen] = useState<HTMLButtonElement | null>(null);
        const [isSizesVisible, setIsSizesVisible] = useState(false);
        const [sizesVisibilityFrame, setSizesVisibilityFrame] = useState<number | null>(null);
        const [mode, setMode] = useState<TooltipEditorModes>(TooltipEditorModes.Basic);

        useEffect(() => {
            setisMasksActive(window.location.pathname.includes('masks'));
            const categoryPath = window.location.pathname
                .replace('/reviews', '')
                .replace('masks', '');

            axios
                .get<ReviewCategoriesResponse>(
                    `${functionUri}?path=${categoryPath}masks&isReview=true`,
                )
                .then(async response => {
                    setIsMasksEnable(Boolean(response.data.Contents.length));
                });
        }, []);

        const isTooltipEditMode =
            mode === TooltipEditorModes.AddTooltipCoords ||
            mode === TooltipEditorModes.AddTooltipSizes;

        const onShowMasks = () => {
            let currentPathname = window.location.pathname;
            if (currentPathname.includes('/masks'))
                window.open(currentPathname.replace('masks', ''), '_self');
            else window.open(currentPathname + 'masks', '_self');
        };

        const changeMode = (mode: TooltipEditorModes) => {
            setIsOpen(false);
            setMode(mode);
            onChangeMode(mode);
        };

        const addTooltip = (text: string) => {
            changeMode(TooltipEditorModes.AddTooltipCoords);
            setIsOpen(false);
            createNewTooltip(text);
        };

        const saveChanges = () => {
            saveNewTooltip();
            changeMode(TooltipEditorModes.Basic);
            setIsOpen(false);
        };

        const discardChanges = () => {
            removeNewTooltip();
            changeMode(TooltipEditorModes.Basic);
            setIsOpen(false);
        };

        const setHideWhileSpinning = (e: React.ChangeEvent<HTMLInputElement>) => {
            const hide = e.target.checked;

            changeOptions({
                ...(hide ? { changeVisibilityNearExtremeFrames: false } : {}),
                hideWhileSpinning: hide,
            });
        };

        const setChangeVisibilityNearExtremeFrames = (e: React.ChangeEvent<HTMLInputElement>) => {
            changeOptions({ changeVisibilityNearExtremeFrames: e.target.checked });
        };

        const onSetFrame = useCallback(
            (frame: number) => {
                setSizesVisibilityFrame(frame);

                setFrame(frame, {
                    animationTime: 500,
                    onReachFrame: () => {
                        setIsSizesVisible(true);
                    },
                });
            },
            [setFrame, setIsSizesVisible],
        );

        const setCanvasSizesRef = (canvas: HTMLCanvasElement | null) => {
            if (canvas && isSizesVisible) {
                const sizesForCurrentFrame = (
                    tooltips
                        .map(({ sizes }) => sizes)
                        .filter(
                            sizes => !!sizes && sizes.frameIndex === currentFrameIndex,
                        ) as TooltipSizesType[]
                )
                    .map(({ sizes }) => sizes)
                    .flat(1);

                drawSizes(canvas, sizesForCurrentFrame);
            }
        };
        useLayoutEffect(() => {
            if (isSizesVisible && currentFrameIndex !== sizesVisibilityFrame) {
                setIsSizesVisible(false);
                setSizesVisibilityFrame(null);
            }
        }, [currentFrameIndex, isSizesVisible, sizesVisibilityFrame]);
        return (
            <>
                <WithAdminMode>
                    <div className={styles.Buttons}>
                        <Button onClick={() => seIsShowTollbar(prev => !prev)}>
                            <ViewListIcon />
                        </Button>
                        {isShowTollbar && (
                            <>
                                <Button
                                    ref={anchorRef}
                                    id='basic-button'
                                    aria-controls={isOpen ? 'basic-menu' : undefined}
                                    aria-haspopup='true'
                                    aria-expanded={isOpen ? 'true' : undefined}
                                    variant='contained'
                                    color='primary'
                                    disabled={!isTooltipEditMode}
                                >
                                    Подсказка
                                </Button>

                                <Popover
                                    className={classnames(styles.MUI, styles.Popover)}
                                    anchorEl={anchorRef.current}
                                    open={isTooltipEditMode}
                                    BackdropProps={{ invisible: true }}
                                    hideBackdrop
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                    }}
                                >
                                    <div className={styles.Popover__content}>
                                        <Typography align='center' variant='h6' component='p'>
                                            {newTooltip?.text}
                                        </Typography>
                                        <Button
                                            fullWidth
                                            onClick={saveChanges}
                                            variant='contained'
                                            color='success'
                                            className={styles.SaveChangesButton}
                                        >
                                            Сохранить
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant='contained'
                                            color='error'
                                            onClick={discardChanges}
                                            className={styles.DiscardChangesButton}
                                        >
                                            Отменить
                                        </Button>
                                    </div>
                                </Popover>

                                <Button
                                    variant='contained'
                                    aria-haspopup='true'
                                    onClick={event => seIsSettingsOpen(event.currentTarget)}
                                    disabled={!tooltips.length}
                                >
                                    Настройки
                                </Button>
                                <Menu
                                    id='simple-menu'
                                    anchorEl={isSettingsOpen}
                                    keepMounted
                                    open={Boolean(isSettingsOpen)}
                                    onClose={() => seIsSettingsOpen(null)}
                                >
                                    <MenuItem>
                                        <FormControlLabel
                                            className={classnames(styles.MUI, styles.checkboxLabel)}
                                            control={
                                                <Checkbox
                                                    checked={hideWhileSpinning}
                                                    disabled={!tooltips.length}
                                                    onChange={setHideWhileSpinning}
                                                    classes={{ root: styles.label }}
                                                />
                                            }
                                            label='Скрывать при вращении'
                                            labelPlacement='start'
                                        />
                                    </MenuItem>
                                    <MenuItem>
                                        <FormControlLabel
                                            className={classnames(styles.MUI, styles.checkboxLabel)}
                                            control={
                                                <Checkbox
                                                    checked={changeVisibilityNearExtremeFrames}
                                                    disabled={hideWhileSpinning || !tooltips.length}
                                                    onChange={setChangeVisibilityNearExtremeFrames}
                                                />
                                            }
                                            label='Менять видимость на крайних кадрах'
                                            labelPlacement='start'
                                        />
                                    </MenuItem>
                                </Menu>
                                <AddTooltip addTooltip={addTooltip} mode={mode} />
                                <DeleteTooltip
                                    tooltips={tooltips}
                                    deleteTooltip={deleteTooltip}
                                    mode={mode}
                                />
                                <FormControlLabel
                                    className={classnames(styles.MUI, styles.switchLabel)}
                                    control={
                                        <Switch
                                            onClick={onShowMasks}
                                            checked={isMasksActive}
                                            disabled={!isMasksEnable}
                                        />
                                    }
                                    label={'Маски'}
                                />
                                <Button
                                    variant='outlined'
                                    onClick={() =>
                                        navigator.clipboard.writeText(
                                            `${clientDomain}${location.pathname}${window.location.search}`,
                                        )
                                    }
                                >
                                    <ContentCopyIcon />
                                </Button>
                            </>
                        )}
                    </div>
                </WithAdminMode>
                <canvas
                    ref={setCanvasSizesRef}
                    width={canvasRef.current?.width}
                    height={canvasRef.current?.height}
                    className={classnames(styles.Sizes, { [styles.visible]: isSizesVisible })}
                />
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        cursor: isTooltipEditMode ? 'crosshair' : 'default',
                    }}
                    onMouseDown={e =>
                        TooltipEditorModes.AddTooltipCoords
                            ? addNewTooltipCoords(e, currentFrameIndex)
                            : () => {}
                    }
                >
                    {tooltips.map(tooltip => (
                        <Tooltip
                            {...tooltip}
                            frameIndex={currentFrameIndex}
                            framesMaxIndex={framesMaxIndex}
                            setFrame={onSetFrame}
                            hideWhileSpinning={hideWhileSpinning}
                            changeVisibilityNearExtremeFrames={changeVisibilityNearExtremeFrames}
                            hidden={isSizesVisible}
                            key={tooltip.text}
                        />
                    ))}
                    {newTooltip && newTooltip.coords.has(currentFrameIndex) && (
                        <Tooltip
                            {...newTooltip}
                            frameIndex={currentFrameIndex}
                            framesMaxIndex={framesMaxIndex}
                            occlude={false}
                        />
                    )}
                </div>
            </>
        );
    },
);

export { Tooltips };
